import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TourFormData, PlaceInfo } from "@/types/tour";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarIcon, MapPin, Beer, Loader2, Navigation, Clock } from "lucide-react";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { PlacesAutocomplete } from "@/components/PlacesAutocomplete";
import { toast } from "sonner";

type Mode = "jetzt" | "startend" | "stadt";

interface TourFormProps {
  onSubmit: (data: TourFormData) => void;
  loading: boolean;
  initialCity?: string;
}

export function TourForm({ onSubmit, loading, initialCity }: TourFormProps) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "de").split("-")[0];
  const dateLocale = lang === "en" ? enUS : de;
  const dateFormat = lang === "en" ? "MM/dd/yyyy" : "dd.MM.yyyy";

  const [mode, setMode] = useState<Mode>("stadt");
  const [barCount, setBarCount] = useState(5);

  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState("20:00");

  const [location, setLocation] = useState(initialCity ?? "");
  const [locationDetails, setLocationDetails] = useState<{ lat: number; lng: number } | null>(null);

  const [startBar, setStartBar] = useState("");
  const [startBarDetails, setStartBarDetails] = useState<PlaceInfo | null>(null);
  const [endLocation, setEndLocation] = useState("");
  const [endLocationDetails, setEndLocationDetails] = useState<PlaceInfo | null>(null);

  const [geoCoords, setGeoCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("form.geo_unsupported"));
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGeoCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
        setGeoLoading(false);
        toast.success(t("form.geo_success"));
      },
      () => {
        setGeoLoading(false);
        toast.error(t("form.geo_error"));
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const isReady = (): boolean => {
    if (mode === "jetzt") return !!geoCoords;
    if (mode === "startend") return !!date && !!startBarDetails;
    return !!date && !!location;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isReady()) return;

    if (mode === "jetzt" && geoCoords) {
      const now = new Date();
      const minutes = Math.ceil(now.getMinutes() / 15) * 15;
      const rounded = new Date(now);
      rounded.setMinutes(minutes, 0, 0);
      if (minutes >= 60) rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
      onSubmit({
        location: t("form.my_location"),
        date: format(now, "yyyy-MM-dd"),
        time: format(rounded, "HH:mm"),
        barCount,
        latitude: geoCoords.lat,
        longitude: geoCoords.lng,
      });
      return;
    }

    if (mode === "startend" && date && startBarDetails) {
      onSubmit({
        location: startBarDetails.name || t("form.tour"),
        date: format(date, "yyyy-MM-dd"),
        time,
        barCount,
        startBar: { name: startBar, ...startBarDetails },
        endLocation: endLocation ? { name: endLocation, ...endLocationDetails } : undefined,
        latitude: startBarDetails.lat || 0,
        longitude: startBarDetails.lng || 0,
      });
      return;
    }

    if (mode === "stadt" && date) {
      onSubmit({
        location,
        date: format(date, "yyyy-MM-dd"),
        time,
        barCount,
        latitude: locationDetails?.lat || 0,
        longitude: locationDetails?.lng || 0,
      });
    }
  };

  const DateTimeFields = (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="min-w-0 space-y-2">
        <Label>{t("form.date")}</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full min-w-0 justify-start overflow-hidden text-left text-sm font-normal",
                !date && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">
                {date ? format(date, dateFormat, { locale: dateLocale }) : t("form.pick_date")}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              initialFocus
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="space-y-2">
        <Label htmlFor="time">{t("form.start_time")}</Label>
        <div className="relative">
          <Clock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground" />
          <Input
            id="time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            required
            className="pl-9 text-sm [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
          />
        </div>
      </div>
    </div>
  );

  return (
    <Card className="border-border/50 bg-card/80 backdrop-blur">
      <CardHeader>
        <h2 className="flex items-center gap-2 text-2xl font-semibold leading-none tracking-tight">
          <Beer className="h-5 w-5 text-primary" aria-hidden="true" />
          {t("form.title")}
        </h2>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-3 pb-5 border-b border-border/50">
            <Label className="flex items-center justify-between">
              <span>{t("form.bar_count")}</span>
              <span className="text-lg font-bold text-primary">{barCount}</span>
            </Label>
            <Slider
              value={[barCount]}
              onValueChange={([val]) => setBarCount(val)}
              min={3}
              max={10}
              step={1}
              className="py-2"
              thumbAriaLabel={t("form.bar_count")}
            />
          </div>

          <div className="space-y-3">
            <Label>{t("form.how_start")}</Label>
            <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)} className="w-full">
              <TabsList className="grid w-full grid-cols-3 h-auto">
                <TabsTrigger value="jetzt" className="flex flex-col gap-1 py-2 text-xs">
                  <span className="text-base">📍</span>
                  <span>{t("form.tab_now")}</span>
                </TabsTrigger>
                <TabsTrigger value="startend" className="flex flex-col gap-1 py-2 text-xs">
                  <span className="text-base">🎯</span>
                  <span>{t("form.tab_startend")}</span>
                </TabsTrigger>
                <TabsTrigger value="stadt" className="flex flex-col gap-1 py-2 text-xs">
                  <span className="text-base">🏙️</span>
                  <span>{t("form.tab_city")}</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="jetzt" className="mt-4">
                <div className="rounded-lg border border-border/50 bg-muted/20 p-4 space-y-3">
                  <p className="text-sm text-muted-foreground text-center">{t("form.now_desc")}</p>
                  <Button
                    type="button"
                    variant={geoCoords ? "outline" : "secondary"}
                    size="lg"
                    className="w-full text-base font-semibold"
                    onClick={detectLocation}
                    disabled={geoLoading || loading}
                  >
                    {geoLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {t("form.detect_loading")}
                      </>
                    ) : geoCoords ? (
                      <>
                        <Navigation className="mr-2 h-5 w-5" />
                        {t("form.detect_done")}
                      </>
                    ) : (
                      <>
                        <Navigation className="mr-2 h-5 w-5" />
                        {t("form.detect_button")}
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="startend" className="mt-4">
                <div className="rounded-lg border border-border/50 bg-muted/20 p-4 space-y-4">
                  {DateTimeFields}
                  <div className="space-y-2">
                    <Label htmlFor="startBar">{t("form.start_bar")}</Label>
                    <PlacesAutocomplete
                      id="startBar"
                      value={startBar}
                      onChange={(val, details) => {
                        setStartBar(val);
                        if (details) {
                          setStartBarDetails({
                            name: details.name,
                            placeId: details.placeId,
                            lat: details.lat,
                            lng: details.lng,
                          });
                        } else {
                          setStartBarDetails(null);
                        }
                      }}
                      placeholder={t("form.start_bar_placeholder")}
                      types={["establishment"]}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endLocation">{t("form.end_location")}</Label>
                    <PlacesAutocomplete
                      id="endLocation"
                      value={endLocation}
                      onChange={(val, details) => {
                        setEndLocation(val);
                        if (details) {
                          setEndLocationDetails({
                            name: details.name,
                            placeId: details.placeId,
                            lat: details.lat,
                            lng: details.lng,
                          });
                        } else {
                          setEndLocationDetails(null);
                        }
                      }}
                      placeholder={t("form.end_location_placeholder")}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="stadt" className="mt-4">
                <div className="rounded-lg border border-border/50 bg-muted/20 p-4 space-y-4">
                  {DateTimeFields}
                  <div className="space-y-2">
                    <Label htmlFor="location">{t("form.city_label")}</Label>
                    <PlacesAutocomplete
                      id="location"
                      value={location}
                      onChange={(val, details) => {
                        setLocation(val);
                        if (details) {
                          setLocationDetails({ lat: details.lat, lng: details.lng });
                        }
                      }}
                      placeholder={t("form.city_placeholder")}
                      types={["(regions)"]}
                      icon={<MapPin className="h-4 w-4" />}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <Button type="submit" className="w-full text-base font-semibold" size="lg" disabled={loading || !isReady()}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t("form.submit_loading")}
              </>
            ) : (
              <>{t("form.submit")}</>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
